<template>
  <div class="column is-6" v-if="produit">
    <div class="card  contactEdition">
      <header class="header-card">
        <p>
          {{ $t('catalogue.newProduct.adresse') }}
        </p>
      </header>
      <div class="card-content">
        <div class="columns is-multiline">
          <b-field :label="$t('address.city')"
                   class="column is-12 is-marginless">
            <gmap-autocomplete
              ref="gmapAddress"
              class="gmap-auto"
              @place_changed="miseAJourCoordonneeAdresse"
              @change.native="autocomplete = $event.target.value"
              :placeholder="$t('catalogue.newProduct.cityPlaceholder')"
              :value="autocomplete"
              :componentRestrictions="{ country: listeAlpha2CodePaysLimiteAutocompleteGoogle }">
            </gmap-autocomplete>
          </b-field>
          <b-field :label="$t('address.zipCode')"
                   class="column is-4 is-marginless">
            <b-input v-model="adresseReseau.codePostal">
            </b-input>
          </b-field>
          <b-field :label="$t('address.city')"
                   class="column is-4 is-marginless obligatoire">
            <b-autocomplete :placeholder="$t('address.city')"
              v-model="adresseReseau.ville"
              :data="villesAutocomplete"
              :loading="isFetching"
              :open-on-focus="true"
              @typing="getVilles"
              @select="option => this.adresseReseau.ville = option">
            </b-autocomplete>
          </b-field>

          <b-field :label="$t('address.country')"
                   class="column is-4 is-marginless">
            <b-input v-model="adresseReseau.pays">
            </b-input>
          </b-field>
          <b-field :label="$t('address.departement')" class="column is-6">
            <b-input v-model="adresseReseau.departement"></b-input>
          </b-field>
          <b-field :label="$t('address.agglomeration')" class="column is-6">
            <b-input v-model="adresseReseau.agglomeration"></b-input>
          </b-field>
        </div>
        <div class="columns mgt-10px">
          <gmap-map class="column is-12 is-height-320" :center="center"
                    :zoom="adresseReseau.longitude !== null && adresseReseau.latitude !== null
                ? zoomDefault : zoomDefaultSansMarker">
            <div v-if="adresseReseau.longitude && adresseReseau.latitude">
              <gmap-marker :key="m.num" v-for="(m) in markers"
                           :position="m.position" :draggable="true" :icon="m.icon"
                           @dragend="updateCoordinates">
              </gmap-marker>
            </div>
          </gmap-map>
        </div>
        <div class="columns">
          <b-field :label="$t('catalogue.lat')" class="column is-4">
            <input-coordonnee v-model="adresseReseau.latitude">
            </input-coordonnee>
          </b-field>
          <b-field :label="$t('catalogue.long')" class="column is-4">
            <input-coordonnee v-model="adresseReseau.longitude">
            </input-coordonnee>
          </b-field>
          <b-field :label="$t('catalogue.couleur')" class="column is-narrow"
                   v-if="produit.supportCommercial.categorieVente
                   === constantes.CATEGORIE_REGROUPEMENT">
            <b-dropdown position="is-top-left">
              <b-dropdown-item>
                <compact v-model="colorFromColorPicker" @input="updateCouleurArgb"/>
              </b-dropdown-item>
              <button slot="trigger" class="button border-radius-4px">
                <!-- L'utilisation du style est normal : cela permet de faciliter la gestion du
                couleur du marker -->
                <b-icon icon="map-marker" :style="{ color: produit.couleurHtmlRgb ?
                produit.couleurHtmlRgb : constantes.COULEUR_PUCE_DEFAULT }">
                </b-icon>
              </button>
            </b-dropdown>
          </b-field>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import { Compact } from 'vue-color';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import getGmapAdressComponentMapping from '@/services/general/map/gmapAdressComponentMapping';
import markersGetter from '@/services/general/map/markersGetter';
import inputCoordonnee from '@/components/general/inputCoordonnee';
import centerMapGetter from '@/services/general/map/centerMapGetter';
import {
  ZOOM_DEFAULT,
  CENTER_DEFAULT,
  ZOOM_DEFAULT_SANS_MARKER,
  COULEUR_PUCE_DEFAULT,
} from '@/configuration/general/carteConf';
import { CATEGORIE_REGROUPEMENT } from '@/configuration/catalogue/produitConf';
import getAllVilles from '@/services/ville/getAllVilles';
import messageErreur from '@/services/general/messageErreur';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';

const Parametres = createNamespacedHelpers('parametres');

export default {
  props: {
    tableName: {
      type: String,
      required: true,
    },
    adresseReseau: {
      type: Object,
      required: true,
    },
    produit: {
      type: Object,
      required: true,
    },
  },
  components: {
    inputCoordonnee,
    Compact,
  },
  data() {
    return {
      zoomDefault: ZOOM_DEFAULT,
      zoomDefaultSansMarker: ZOOM_DEFAULT_SANS_MARKER,
      autocomplete: '',
      villesAutocomplete: [],
      isFetching: false,
      gmapAddressComponentMapping: getGmapAdressComponentMapping(),
      colorFromColorPicker: {
        hex: this.produit.couleurHtmlRgb ? this.produit.couleurHtmlRgb : COULEUR_PUCE_DEFAULT,
      },
      constantes: {
        COULEUR_PUCE_DEFAULT,
        CATEGORIE_REGROUPEMENT,
      },
    };
  },
  computed: {
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
    }),
    markers() {
      return markersGetter(this.adresseReseau);
    },
    center() {
      if (this.adresseReseau.longitude !== null
        && this.adresseReseau.latitude !== null) {
        return centerMapGetter(this.adresseReseau, this.markers);
      }
      return centerMapGetter(CENTER_DEFAULT, []);
    },
    listeAlpha2CodePaysLimiteAutocompleteGoogle() {
      const param = this.parametresInterne(
        PARAMETRES_INTERNE_KEYS.listeAlpha2CodePaysLimiteAutocompleteGoogle.key,
      );
      if (!param.length) return [];
      return param.split('|').map(el => el.trim());
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
    }),
    getVilles: debounce(async function getVilles(villeName) {
      try {
        this.isFetching = true;
        this.villesAutocomplete = await getAllVilles(villeName, this.tableName);
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      } finally {
        this.isFetching = false;
      }
    }, 300),
    updateCouleurArgb(newColor) {
      this.produit.couleurHtmlRgba = cloneDeep(newColor.hex8);
      this.produit.couleurHtmlRgb = cloneDeep(newColor.hex);
    },
    updateCoordinates(location) {
      this.adresseReseau.longitude = location.latLng.lng();
      this.adresseReseau.latitude = location.latLng.lat();
    },
    miseAJourCoordonneeAdresse(place) {
      Object.keys(this.adresseReseau).forEach(key => {
        this.adresseReseau[key] = '';
      });
      try {
        this.adresseReseau.latitude = place.geometry.location.lat();
        this.adresseReseau.longitude = place.geometry.location.lng();
      } catch {
        this.adresseReseau.latitude = null;
        this.adresseReseau.longitude = null;
      }
      place.address_components.forEach(element => {
        element.types.forEach(type => {
          if (Object.prototype.hasOwnProperty.call(this.gmapAddressComponentMapping, type)) {
            const mapping = this.gmapAddressComponentMapping[type];
            this.adresseReseau[mapping.field] += element.long_name;
            if (mapping.separator) {
              this.adresseReseau[mapping.field] += mapping.separator;
            }
          }
        });
      });
    },
  },
  async mounted() {
    try {
      await this.loadParametresInterne(
        [PARAMETRES_INTERNE_KEYS.listeAlpha2CodePaysLimiteAutocompleteGoogle.key],
      );
    } catch (e) {
      messageErreur(this.$i18n);
      throw e;
    }
  },
};
</script>
